"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button, Input } from "@repo/ui";
import { Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import { ErrorHandler } from "@repo/errors";
import { clientLogger } from "@repo/observability/client";
import { OtpInput } from "./OtpInput";

interface LoginFormProps {
  redirectTo?: string;
  onSuccess?: () => void;
  fromQuiz?: boolean;
  defaultToMagicLink?: boolean;
  userEmail?: string;
  emailReadOnly?: boolean;
  updateUserData?: {
    timezone?: string;
    // Add more fields here in the future as needed
  };
  // Suppress the built-in "Don't have an account? Sign up" link.
  // Set when a parent (e.g. MagicLinkPrompt) renders its own signup
  // prompt with context-specific routing (stashing a pending booking
  // + attaching a proper returnTo). Avoids the duplicate link.
  hideSignupLink?: boolean;
}

export function LoginForm({ redirectTo = "/dashboard", onSuccess, fromQuiz, defaultToMagicLink = true, userEmail, emailReadOnly = false, updateUserData, hideSignupLink = false }: LoginFormProps) {
  const router = useRouter();
  // Debug logging
  console.log('[LoginForm] Component mounted with redirectTo:', redirectTo);
  console.log('[LoginForm] If this contains allowBooking=true, it should be preserved');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [useOtp, setUseOtp] = useState(defaultToMagicLink);
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  const [formData, setFormData] = useState({
    email: userEmail || "",
    password: ""
  });

  // Check if coming from quiz with a success message
  useEffect(() => {
    if (fromQuiz) {
      setSuccess("Account created! Please sign in to continue.");
    }
  }, [fromQuiz]);

  // Handle OTP verification
  const handleOtpComplete = async (otp: string) => {
    setVerifyingOtp(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: otpEmail, token: otp })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Invalid verification code');
        setVerifyingOtp(false);
      } else {
        setSuccess("Login successful! Redirecting...");

        // Update user data if provided (e.g., from quiz flow)
        if (updateUserData && Object.keys(updateUserData).length > 0) {
          try {
            const updateResponse = await fetch('/api/client/users/profile', {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...updateUserData
              })
            });

            if (updateResponse.ok) {
              const { user: updatedUser } = await updateResponse.json();
              console.log('[LoginForm] Updated user data:', updatedUser);
            } else {
              const { error } = await updateResponse.json();
              console.error('[LoginForm] Error updating user data:', error);
            }
          } catch (error) {
            console.error('[LoginForm] Error updating user data:', error);
            await clientLogger.error(error, {
              componentName: 'LoginForm',
              action: 'updateUserData',
              feature: 'auth',
              userImpact: 'blocked'
            });
          }
        }

        if (onSuccess) {
          onSuccess();
        }

        // UserContext will handle loading the profile
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 1000);
      }
    } catch (err) {
      setError("Failed to verify code. Please try again.");
      setVerifyingOtp(false);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError(null);
      return true;
    }
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleOtp = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: formData.email })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // No account yet — funnel the visitor through /signup with email
        // pre-filled and returnTo set so they end up back where they were.
        if (data.error?.toLowerCase().includes('no account found')) {
          const params = new URLSearchParams({
            email: formData.email,
            returnTo: redirectTo,
          });
          router.replace(`/signup?${params.toString()}`);
          return;
        }
        setError(data.error || 'Failed to send code');
        setIsLoading(false);
      } else {
        setOtpSent(true);
        setOtpEmail(formData.email);
        setSuccess(`We've sent a 6-digit code to ${formData.email}`);
        setIsLoading(false);
      }
    } catch (err) {
      const appError = ErrorHandler.normalize(err);
      ErrorHandler.log(appError);
      await clientLogger.error(err, {
        componentName: 'LoginForm',
        action: 'handleOtpSubmit',
        metadata: { email: formData.email },
        feature: 'auth',
        userImpact: 'blocked'
      });
      setError(ErrorHandler.getUserMessage(appError));
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateEmail(formData.email)) {
      return;
    }

    if (useOtp) {
      await handleOtp();
      return;
    }

    setIsLoading(true);

    try {
      // Use server-side login API (same pattern as therapist app)
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important: send cookies
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        const errorMessage = data.error || 'Login failed';
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      // Update user data if provided (e.g., from quiz flow)
      if (updateUserData && Object.keys(updateUserData).length > 0) {
        try {
          // Use API to update user profile
          const updateResponse = await fetch('/api/client/users/profile', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...updateUserData
            })
          });

          if (updateResponse.ok) {
            const { user: updatedUser } = await updateResponse.json();
            console.log('[LoginForm] Updated user data:', updatedUser);
          } else {
            const { error } = await updateResponse.json();
            console.error('[LoginForm] Error updating user data:', error);
          }
        } catch (error) {
          console.error('[LoginForm] Error updating user data:', error);
          await clientLogger.error(error, {
            componentName: 'LoginForm',
            action: 'updateUserData',
            feature: 'auth',
            userImpact: 'blocked'
          });
        }
      }

      if (onSuccess) {
        onSuccess();
      }

      // Use window.location.href for full page navigation (same as therapist app)
      // This ensures middleware runs and session is properly established
      console.log('[LoginForm] Login successful, redirecting to:', redirectTo);
      window.location.href = redirectTo;
    } catch (err) {
      await clientLogger.error(err, {
        componentName: 'LoginForm',
        action: 'handlePasswordSubmit',
        feature: 'auth',
        userImpact: 'blocked'
      });
      setError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
    
    // Validate email on change
    if (name === "email") {
      validateEmail(value);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
          {otpSent && (
            <div className="mt-4 space-y-4">
              <div className="text-center">
                {verifyingOtp ? (
                  <div className="flex flex-col items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <p className="mt-3 text-sm text-gray-700 font-medium">Signing you in...</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-3">Enter the 6-digit code we sent to your email</p>
                    <OtpInput
                      onComplete={handleOtpComplete}
                      disabled={verifyingOtp}
                      error={!!error}
                    />
                  </>
                )}
              </div>
              {!verifyingOtp && (
                <div className="flex flex-col gap-2 items-center">
                  <button
                    type="button"
                    onClick={async () => {
                      setOtpSent(false);
                      setSuccess(null);
                      setError(null);
                      await handleOtp();
                    }}
                    disabled={isLoading}
                    className="text-sm text-green-600 hover:text-green-700 underline"
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUseOtp(false);
                      setOtpSent(false);
                      setSuccess(null);
                      setError(null);
                    }}
                    className="text-sm text-gray-600 hover:text-gray-700 underline"
                  >
                    Use password instead
                  </button>
                </div>
              )}
              <div className="text-xs text-gray-500 text-center">
                <p>✓ Works on any browser or device</p>
                <p>✓ Code expires in 10 minutes</p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!otpSent && (
        <div>
          <Input
            type="email"
            name="email"
            label="Email address"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="you@example.com"
            error={emailError || undefined}
            required
            autoComplete="email"
            className={emailError ? "border-red-300 focus:border-red-500" : ""}
            disabled={emailReadOnly}
          />
        </div>
      )}

      {!useOtp && !otpSent && (
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          name="password"
          label="Password"
          value={formData.password}
          onChange={handleInputChange}
          placeholder="Enter your password"
          required
          autoComplete="current-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
      </div>
      )}

      <div className="flex items-center justify-between">
        {!useOtp && !otpSent ? (
          <>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="ml-2 text-sm text-gray-600 select-none">Remember me</span>
            </label>
            <Link
              href="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-500 font-medium transition-colors"
            >
              Forgot password?
            </Link>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setUseOtp(false);
              setOtpSent(false);
              setSuccess(null);
            }}
            className="text-sm text-primary-600 hover:text-primary-500 font-medium transition-colors"
          >
            Sign in with password instead
          </button>
        )}
      </div>

      {!otpSent && (
        <Button
          type="submit"
          className="w-full"
          isLoading={isLoading}
          disabled={!formData.email || (!useOtp && !formData.password) || !!emailError}
        >
          {isLoading ? (useOtp ? "Sending code..." : "Signing in...") :
           useOtp ? "Send verification code" : "Sign in"}
        </Button>
      )}

      {!useOtp && !otpSent && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setUseOtp(true)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Sign in without password
          </button>
        </div>
      )}

      {!otpSent && !hideSignupLink && (
        <div className="text-center text-sm text-gray-600">
          Don&apos;t have an account?{" "}
          <Link
            href={`/signup?email=${encodeURIComponent(formData.email)}&returnTo=${encodeURIComponent(redirectTo)}`}
            className="font-semibold text-primary-600 hover:text-primary-500"
          >
            Sign up
          </Link>
        </div>
      )}
    </form>
  );
}